/**
 * CellEditor.jsx - the popover for one calendar cell: put an engineer on a
 * project over a date range (picking an existing label or typing a new one),
 * with where they are, under which Global Project Number and in what status -
 * or clear those days.
 *
 * Location / Project # / Status belong to the scheduled days, not to the
 * label: the server copies today's onto the engineer's card.
 *
 * The Project # is picked from the real projects (won jobs with a Global
 * Project Number) rather than typed from memory: a typed one that matches no
 * project gets a warning, and the picked number can go on this engineer's
 * neighbouring blocks of the same label - or every block of that label still
 * missing one - in the same save (10/11/26).
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { useDraggableDialog } from '../../hooks/useDraggableDialog';
import { createPortal } from 'react-dom';
import { Icon } from '../ui/Icon';
import { isTimeOff } from '../../utils/utilizationFromCalendar';
import { MAX_PROJECT_NUMBER, findJob, isNonProjectLabel, jobCaption, searchJobs } from './projectNumbers';

/** The engineer-card status vocabulary, exactly. */
export const STATUS_OPTIONS = ['On-Site', 'Office', 'PTO'];

/**
 * Inline editor that opens when the user clicks a cell (or a run of cells).
 * Lets them set the date range, pick or type a project, fill in its details,
 * save, or clear.
 *
 * initial.detail     the block's saved details, when opened on one
 * suggestDetail(lbl) optional: the details of that label's most recent block,
 *                    which the fields take whenever a different label is
 *                    picked (except the ones typed in)
 * onSave(from, to, project, { location, projectNumber, status })
 * jobs               optional: the real projects to pick a Project # from
 *                    (projectNumbers.jobOptions)
 * fillCandidates(scope, label, from, to)
 *                    optional: the other days ('adjacent' | 'label') a picked
 *                    number could also go on (projectNumbers.fillCandidates)
 * onFill(keys, label, projectNumber)
 *                    optional: puts the number on those days, after onSave
 */
export function CellEditor({
  initial,
  allProjects,
  colorFor,
  suggestDetail,
  onSave,
  onClear,
  onClose,
  jobs = NO_JOBS,
  fillCandidates,
  onFill,
}) {
  const ref = useRef(null);
  const drag = useDraggableDialog(true, { ref }); // moved by its header (10/9/26)
  const [from, setFrom] = useState(initial.dateFrom);
  const [to, setTo] = useState(initial.dateTo);
  const [project, setProject] = useState(initial.initialCode || '');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const d0 = initial.detail || {};
  const [location, setLocation] = useState(d0.location || '');
  const [projectNumber, setProjectNumber] = useState(d0.projectNumber || '');
  // A PTO block saved before details existed still opens as PTO.
  const [status, setStatus] = useState(d0.status || (isTimeOff(initial.initialCode) ? 'PTO' : ''));
  // Which of the three fields the user has typed in (or, for Status, picked)
  // since they were last filled automatically - on opening, or by choosing a
  // label. Those are never overwritten; the others follow the label.
  const typed = useRef({ location: false, projectNumber: false, status: false });
  const pto = status === 'PTO';
  const [jobQuery, setJobQuery] = useState('');
  // A project picked while the label was empty: its name, offered as the label.
  const [labelOffer, setLabelOffer] = useState('');
  // "Also fill": this engineer's neighbouring blocks of the label (on by
  // default - the office fills these a stint at a time), and every block of
  // the label missing a number (off: it reaches other engineers).
  const [fillAdjacent, setFillAdjacent] = useState(true);
  const [fillLabel, setFillLabel] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  // YYYY-MM-DD strings compare correctly as strings
  const rangeOk = !!from && !!to && from <= to;
  const canSave = !!project && rangeOk;

  const chooseProject = (p) => {
    if (p === project) return;
    setProject(p);
    // Time off has no site and no job number, whatever was typed.
    if (isTimeOff(p)) {
      setStatus('PTO');
      setLocation('');
      setProjectNumber('');
      typed.current = { location: false, projectNumber: false, status: false };
      return;
    }
    // A different job. What the fields hold now belongs to the old one (the
    // block this was opened on - e.g. today's, from the card's Schedule - or
    // the last label picked), and saving it onto the new job was the bug
    // (r49 fix F4). Start from this job's last block, blank when it has none.
    const s = (suggestDetail && suggestDetail(p)) || {};
    const t = typed.current;
    if (!t.status) setStatus(s.status || '');
    // a PTO picked by hand keeps Location / Project # empty (changeStatus)
    if (t.status && status === 'PTO') return;
    if (!t.location) setLocation(s.location || '');
    if (!t.projectNumber) setProjectNumber(s.projectNumber || '');
  };
  const changeStatus = (v) => {
    typed.current.status = true;
    setStatus(v);
    if (v === 'PTO') {
      // emptied by the PTO rule, not typed: a later label may fill them again
      setLocation('');
      setProjectNumber('');
      typed.current.location = false;
      typed.current.projectNumber = false;
    }
  };

  const pickJob = (job) => {
    typed.current.projectNumber = true;
    setProjectNumber(job.gpn);
    setJobQuery('');
    setLabelOffer(project ? '' : job.name || job.gpn);
  };

  const num = projectNumber.trim();
  const numberedJob = useMemo(() => findJob(jobs, num), [jobs, num]);
  // Only when the list could know: a user who cannot read projects has none.
  const unknownNumber = !pto && !!num && jobs.length > 0 && !numberedJob;
  const matches = useMemo(() => searchJobs(jobs, jobQuery, project), [jobs, jobQuery, project]);
  const canFill = !pto && !!num && !!project && !!fillCandidates && !!onFill && !isNonProjectLabel(project) && rangeOk;
  const adjacentKeys = useMemo(
    () => (canFill ? fillCandidates('adjacent', project, from, to) : []),
    [canFill, fillCandidates, project, from, to]
  );
  const labelKeys = useMemo(
    () => (canFill ? fillCandidates('label', project, from, to) : []),
    [canFill, fillCandidates, project, from, to]
  );
  const labelEngineers = new Set(labelKeys.map((k) => k.slice(0, k.lastIndexOf('|')))).size;

  const detail = () =>
    pto ? { location: '', projectNumber: '', status } : { location: location.trim(), projectNumber: projectNumber.trim(), status };

  const apply = () => {
    if (!canSave) return;
    onSave(from, to, project, detail());
    if (canFill) {
      const keys = [...new Set([...(fillAdjacent ? adjacentKeys : []), ...(fillLabel ? labelKeys : [])])];
      if (keys.length) onFill(keys, project, num);
    }
    onClose();
  };
  const clear = () => {
    if (!rangeOk) return;
    onClear(from, to);
    onClose();
  };
  // A typed name is picked, not saved straight away: the details below still
  // need filling in, and Save does the rest.
  const submitNew = () => {
    const n = newName.trim();
    if (!n) return;
    chooseProject(n);
    setAdding(false);
    setNewName('');
  };
  // ...so a new name has to show up in the picker, selected.
  const choices = project && !allProjects.includes(project) ? [project, ...allProjects] : allProjects;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(13,13,13,0.45)',
      }}
    >
      <div
        ref={ref}
        onPointerDown={drag.onPointerDown}
        className="go-pop-in"
        style={{
          background: '#fff',
          borderRadius: 12,
          width: 460,
          maxHeight: '85vh',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          ...drag.style,
        }}
      >
        {/* Header */}
        <div data-dialog-handle="" style={{ padding: '14px 18px', borderBottom: '1px solid #E5E9DD' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#1B5E20', letterSpacing: 1.6, textTransform: 'uppercase' }}>
                Edit assignment
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#0D0D0D', marginTop: 2 }}>{initial.engineerName}</div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: '#fff',
                border: '1px solid #E5E9DD',
                color: '#444',
                width: 30,
                height: 30,
                borderRadius: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="x" size={13} color="#444" />
            </button>
          </div>
        </div>

        {/* Date range */}
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #F4F6F0' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: '#888', letterSpacing: 1.3, textTransform: 'uppercase', marginBottom: 8 }}>
            Date range
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={dateInputStyle} />
            <span style={{ color: '#888', fontWeight: 600 }}>to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={dateInputStyle} />
          </div>
          {!rangeOk && (
            <div style={{ marginTop: 7, fontSize: 10.5, fontWeight: 700, color: '#B71C1C' }}>
              {from && to ? 'The end date is before the start date.' : 'Pick both dates.'}
            </div>
          )}
        </div>

        {/* Project picker */}
        <div style={{ padding: '14px 18px', overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: '#888', letterSpacing: 1.3, textTransform: 'uppercase' }}>Project</div>
            {!adding && (
              <button
                onClick={() => setAdding(true)}
                style={{
                  border: '1px solid #1B5E20',
                  background: '#fff',
                  color: '#1B5E20',
                  padding: '4px 10px',
                  borderRadius: 6,
                  fontSize: 10.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                }}
              >
                + New project
              </button>
            )}
          </div>
          {adding ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                autoFocus
                placeholder="New project name (e.g. KSA Special Build)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitNew();
                }}
                style={{
                  flex: 1,
                  padding: '7px 10px',
                  borderRadius: 6,
                  border: '1px solid #E5E9DD',
                  fontSize: 12,
                  fontFamily: 'Montserrat',
                }}
              />
              <button
                onClick={submitNew}
                style={{
                  border: '1px solid #0F3D14',
                  background: '#1B5E20',
                  color: '#fff',
                  padding: '7px 14px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Add
              </button>
              <button
                onClick={() => {
                  setAdding(false);
                  setNewName('');
                }}
                style={{
                  border: '1px solid #E5E9DD',
                  background: '#fff',
                  color: '#666',
                  padding: '7px 10px',
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 6,
                maxHeight: 280,
                overflowY: 'auto',
                border: '1px solid #F0F2EC',
                borderRadius: 6,
                padding: 6,
                background: '#FAFBF7',
              }}
            >
              {choices.map((p) => (
                <button
                  key={p}
                  onClick={() => chooseProject(p)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: project === p ? '1px solid #1B5E20' : '1px solid #E5E9DD',
                    background: project === p ? '#E8F5E9' : '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'JetBrains Mono, monospace',
                    color: '#0D0D0D',
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 3,
                      background: colorFor(p),
                      flexShrink: 0,
                      border: '1px solid rgba(0,0,0,0.1)',
                    }}
                  />
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Where, under which job number, and in what status. All optional:
            a Global Project Number is often not issued yet. */}
        <div
          style={{
            padding: '12px 18px 14px',
            borderTop: '1px solid #F4F6F0',
            display: 'grid',
            gridTemplateColumns: '1fr 120px',
            gap: '10px 10px',
            overflowY: 'auto',
            flex: '0 1 auto',
            minHeight: 0,
          }}
        >
          <div style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="cell-editor-location" style={fieldLabelStyle}>
              Location
            </label>
            <input
              id="cell-editor-location"
              value={location}
              disabled={pto}
              placeholder={pto ? '' : 'Site or office'}
              onChange={(e) => {
                typed.current.location = true;
                setLocation(e.target.value);
              }}
              style={fieldStyle(pto)}
            />
          </div>
          <div>
            <label htmlFor="cell-editor-gpn" style={fieldLabelStyle}>
              Global Project Number
            </label>
            <input
              id="cell-editor-gpn"
              value={projectNumber}
              disabled={pto}
              placeholder={pto ? '' : 'Pick below or type'}
              maxLength={MAX_PROJECT_NUMBER}
              onChange={(e) => {
                typed.current.projectNumber = true;
                setProjectNumber(e.target.value);
                setLabelOffer('');
              }}
              style={{ ...fieldStyle(pto), ...(unknownNumber ? { border: '1px solid #E65100' } : null) }}
            />
          </div>
          <div>
            <label htmlFor="cell-editor-status" style={fieldLabelStyle}>
              Status
            </label>
            <select id="cell-editor-status" value={status} onChange={(e) => changeStatus(e.target.value)} style={fieldStyle(false)}>
              <option value="">-</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {!pto && numberedJob && (
            <div data-testid="gpn-match" style={{ gridColumn: '1 / -1', fontSize: 10.5, fontWeight: 700, color: '#1B5E20' }}>
              {'\u2713 '}
              {jobCaption(numberedJob)}
            </div>
          )}
          {unknownNumber && (
            <div role="alert" style={{ gridColumn: '1 / -1', fontSize: 10.5, fontWeight: 700, color: '#E65100' }}>
              {`${num} matches no project in GlobalOps. Check the number, or pick the project below.`}
            </div>
          )}
          {!pto && labelOffer && !project && (
            <div
              style={{
                gridColumn: '1 / -1',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 10.5,
                fontWeight: 700,
                color: '#444',
              }}
            >
              <span>No label yet.</span>
              <button
                type="button"
                onClick={() => {
                  chooseProject(labelOffer);
                  setLabelOffer('');
                }}
                style={smallBtn}
              >
                {`Use "${labelOffer}" as the label`}
              </button>
            </div>
          )}

          {!pto && jobs.length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label htmlFor="cell-editor-job-search" style={fieldLabelStyle}>
                Pick the project
              </label>
              <input
                id="cell-editor-job-search"
                value={jobQuery}
                placeholder="Search by number, name or customer"
                onChange={(e) => setJobQuery(e.target.value)}
                style={fieldStyle(false)}
              />
              <div
                role="listbox"
                aria-label="Projects"
                style={{
                  marginTop: 6,
                  maxHeight: 150,
                  overflowY: 'auto',
                  border: '1px solid #F0F2EC',
                  borderRadius: 6,
                  background: '#FAFBF7',
                }}
              >
                {matches.length === 0 && (
                  <div style={{ padding: '8px 10px', fontSize: 10.5, color: '#888', fontWeight: 600 }}>No project matches that.</div>
                )}
                {matches.map((j) => {
                  const on = !!numberedJob && numberedJob.gpn === j.gpn;
                  return (
                    <button
                      key={j.gpn}
                      type="button"
                      role="option"
                      aria-selected={on}
                      onClick={() => pickJob(j)}
                      title={j.active ? jobCaption(j) : `${jobCaption(j)} (closed)`}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '5px 10px',
                        border: 'none',
                        borderBottom: '1px solid #F0F2EC',
                        cursor: 'pointer',
                        background: on ? '#E8F5E9' : 'transparent',
                        color: j.active ? '#0D0D0D' : '#888',
                        fontSize: 10.5,
                        fontWeight: 600,
                        fontFamily: 'Montserrat',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {jobCaption(j)}
                      {!j.active && ' (closed)'}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {canFill && (adjacentKeys.length > 0 || labelKeys.length > 0) && (
            <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {adjacentKeys.length > 0 && (
                <label style={checkRow}>
                  <input type="checkbox" checked={fillAdjacent} onChange={(e) => setFillAdjacent(e.target.checked)} />
                  {`Also put ${num} on ${initial.engineerName}'s neighbouring ${project} days without a # (${adjacentKeys.length})`}
                </label>
              )}
              {labelKeys.length > 0 && (
                <label style={checkRow}>
                  <input type="checkbox" checked={fillLabel} onChange={(e) => setFillLabel(e.target.checked)} />
                  {`Also put ${num} on every ${project} day missing a # (${labelKeys.length} day${labelKeys.length === 1 ? '' : 's'}, ${labelEngineers} engineer${labelEngineers === 1 ? '' : 's'})`}
                </label>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid #E5E9DD',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <button
            onClick={clear}
            style={{
              border: '1px solid #FFCDD2',
              background: '#fff',
              color: '#B71C1C',
              padding: '7px 14px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Remove assignment
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onClose}
              style={{
                border: '1px solid #E5E9DD',
                background: '#fff',
                color: '#666',
                padding: '7px 14px',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={apply}
              disabled={!canSave}
              style={{
                border: '1px solid #0F3D14',
                background: canSave ? '#1B5E20' : '#F4F6F0',
                color: canSave ? '#fff' : '#BBB',
                padding: '7px 14px',
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 700,
                cursor: canSave ? 'pointer' : 'not-allowed',
              }}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const NO_JOBS = [];

const dateInputStyle = {
  flex: 1,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #E5E9DD',
  fontSize: 12,
  fontFamily: 'Montserrat',
  color: '#0D0D0D',
};

const fieldLabelStyle = {
  display: 'block',
  fontSize: 10,
  fontWeight: 800,
  color: '#888',
  letterSpacing: 1.3,
  textTransform: 'uppercase',
  marginBottom: 5,
};

const fieldStyle = (disabled) => ({
  ...dateInputStyle,
  width: '100%',
  boxSizing: 'border-box',
  height: 32,
  background: disabled ? '#F4F6F0' : '#fff',
  cursor: disabled ? 'not-allowed' : 'auto',
});

const smallBtn = {
  border: '1px solid #1B5E20',
  background: '#fff',
  color: '#1B5E20',
  padding: '3px 9px',
  borderRadius: 6,
  fontSize: 10.5,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
};

const checkRow = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 10.5,
  fontWeight: 600,
  color: '#333',
  cursor: 'pointer',
};
